import React from "react";

import { css, cx } from "@emotion/css";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { alertCategoriesTranslations, AlertConfig } from "@/models/AlertModel.ts";
import { AlertSettingsConditions } from "@/pages/patient-settings/alert-settings/AlertSettingsConditions.tsx";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { TextToast } from "@/uiKit/atoms/Toast.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

export type AlertSettingsRowProps = {
  patientId: string;
  alertConfig: AlertConfig;
  className?: string;
  classes?: {
    typeCol?: string;
    conditionsCol?: string;
    actionCol?: string;
  };
};

export const AlertSettingsRow: React.FC<AlertSettingsRowProps> = ({ patientId, alertConfig, className, classes }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const deleteAlertConfig = Queries.alerts.useDeleteAlertConfig();

  return (
    <TableRow id={`alert-config-${alertConfig.id}`} className={cx(styles.container, className)}>
      <TableCell className={classes?.typeCol}>
        <Typography variant="body">{t(alertCategoriesTranslations[alertConfig.category])}</Typography>
      </TableCell>
      <TableCell className={classes?.conditionsCol}>
        <AlertSettingsConditions patientId={patientId} alertConfig={alertConfig} />
      </TableCell>
      <TableCell className={cx("ActionCell", classes?.actionCol, styles.actions)}>
        {alertConfig.canDelete ? (
          <Button
            data-action="patient-settings.alert.delete"
            variant="text"
            onClick={() =>
              deleteAlertConfig.mutate(
                {
                  patientId,
                  alertConfigId: alertConfig.id,
                },
                {
                  onSuccess: () => {
                    TextToast.success(
                      t("alertConfig.deleteSuccess", {
                        alert: t(alertCategoriesTranslations[alertConfig.category]),
                      }),
                    );
                  },
                  onError: () => {
                    TextToast.error(
                      t("alertConfig.deleteError", {
                        alert: t(alertCategoriesTranslations[alertConfig.category]),
                      }),
                    );
                  },
                },
              )
            }
            startIcon={<Icons.trash className={styles.icon} />}
          />
        ) : null}
      </TableCell>
    </TableRow>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    .ActionCell > * {
      visibility: hidden;
    }
    :hover {
      background-color: ${theme.palette.grey["50"]};

      .ActionCell > * {
        visibility: visible;
      }
    }
  `,
  typeColumn: css`
    display: flex;
    justify-content: stretch;
    align-items: center;
  `,
  actions: css`
    padding: 0;
  `,
  icon: css({
    width: FontSizes.body,
    height: FontSizes.body,
  }),
});
